import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FileSpreadsheet, AlertTriangle, Download, CheckCircle2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { justoAPI, ESTADOS_JUSTO, TIPO_LABEL, blobErrorDetail } from '@/lib/justo';
import { downloadBlob, filenameFromHeaders, fmtNum, formatFechaHora, errorMsg } from '@/lib/recuentos';

const Faltantes = ({ items, isAdmin }) => (
  <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm" data-testid="justo-faltantes">
    <p className="font-semibold text-red-700 mb-1">No se puede generar: {items.length} productos sin equivalencia Justo</p>
    <ul className="list-disc pl-5 text-red-700 max-h-32 overflow-y-auto">
      {items.map((f) => <li key={f.codigo + f.nombre}>{f.codigo} · {f.nombre} ({fmtNum(f.cantidad)} {f.unidad})</li>)}
    </ul>
    {isAdmin
      ? <Link to="/admin/justo?tab=equivalencias" className="inline-block mt-2 font-semibold text-red-700 underline" data-testid="justo-ir-equivalencias">Completar en Equivalencias Justo</Link>
      : <p className="mt-2 text-red-600">Pide al administrador que complete las equivalencias.</p>}
  </div>
);

const CorregirOrigen = ({ p, onDone }) => {
  const [wid, setWid] = useState('');
  const [saving, setSaving] = useState(false);
  const guardar = async () => {
    setSaving(true);
    try { await justoAPI.corregirOrigen(p.tipo, p.id, wid); toast.success('Bodega de origen corregida'); await onDone(); }
    catch (e) { toast.error(errorMsg(e, 'No se pudo corregir')); }
    finally { setSaving(false); }
  };
  return (
    <div className="rounded-lg border border-orange-200 bg-orange-50 p-3 space-y-2" data-testid="justo-corregir-origen">
      <p className="text-sm font-semibold text-orange-800">Corregir bodega de origen</p>
      <p className="text-xs text-orange-700">Elige la bodega desde donde salió realmente este despacho.</p>
      <div className="flex gap-2">
        <Select value={wid} onValueChange={setWid}>
          <SelectTrigger className="bg-white" data-testid="justo-origen-select"><SelectValue placeholder="Elegir bodega" /></SelectTrigger>
          <SelectContent>
            {p.origenes_disponibles.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button onClick={guardar} disabled={!wid || saving} className="bg-orange-500 hover:bg-orange-600" data-testid="justo-origen-guardar-btn">Guardar</Button>
      </div>
    </div>
  );
};

const Filas = ({ filas }) => (
  <table className="w-full text-sm" data-testid="justo-preview-table">
    <thead className="bg-slate-50 text-slate-600"><tr>
      <th className="px-2 py-1.5 text-left">Ingrediente Justo</th><th className="px-2 py-1.5 text-right">Cantidad</th>
    </tr></thead>
    <tbody>
      {filas.map((f) => (
        <tr key={f.id_justo} className="border-t">
          <td className="px-2 py-1.5"><div>{f.nombre_justo}</div><div className="text-[11px] text-slate-400">{f.origen.join(' + ')}</div></td>
          <td className="px-2 py-1.5 text-right font-semibold whitespace-nowrap">{fmtNum(f.cantidad)} {f.unidad}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

const Avisos = ({ p }) => (
  <>
    {p.estado_justo === 'subido_justo' && (
      <div className="rounded-lg bg-amber-50 border border-amber-300 p-3 text-sm text-amber-800 flex gap-2" data-testid="justo-aviso-subido">
        <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
        Este envío ya fue subido a Justo ({formatFechaHora(p.justo_export?.fecha_subido)}). Si lo vuelves a subir quedará duplicado.
      </div>
    )}
    {p.estado_justo === 'modificado' && (
      <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700 flex gap-2" data-testid="justo-aviso-modificado">
        <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
        El envío cambió después de exportarse ({formatFechaHora(p.justo_export?.fecha_exportacion)}){p.justo_export?.subido ? ' (y ya estaba subido a Justo: corrige la transferencia en Justo)' : ''}. Exporta de nuevo.
      </div>
    )}
  </>
);

export const ExportJustoButton = ({ tipo, id, size = 'sm', onChanged }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);

  const open = async () => {
    setBusy(true);
    try { setP((await justoAPI.preview(tipo, id)).data); } catch (e) { toast.error(errorMsg(e, 'No se pudo preparar la exportación')); }
    finally { setBusy(false); }
  };

  const refresh = async () => { setP((await justoAPI.preview(tipo, id)).data); onChanged && onChanged(); };

  const descargar = async () => {
    if (p.estado_justo === 'subido_justo' && !window.confirm('Este envío ya fue subido a Justo. ¿Generar el archivo de nuevo?')) return;
    setBusy(true);
    try {
      const res = await justoAPI.exportar(tipo, id);
      downloadBlob(res.data, filenameFromHeaders(res.headers, p.filename));
      toast.success('Transferencia generada');
      await refresh();
    } catch (e) {
      const d = await blobErrorDetail(e);
      toast.error(typeof d === 'string' ? d : d?.message || 'Error al exportar');
    } finally { setBusy(false); }
  };

  const marcar = async () => {
    try { await justoAPI.marcarSubido(tipo, id); toast.success('Marcado como subido a Justo'); await refresh(); }
    catch (e) { toast.error(errorMsg(e, 'Error')); }
  };

  return (
    <>
      <Button size={size} variant="outline" onClick={open} disabled={busy} className="text-green-700 border-green-200 hover:bg-green-50" data-testid={`export-justo-btn-${id}`}>
        <FileSpreadsheet className="w-4 h-4 mr-1" /> Exportar para Justo
      </Button>
      <Dialog open={!!p} onOpenChange={(o) => !o && setP(null)}>
        <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto" data-testid="export-justo-dialog">
          <DialogHeader><DialogTitle>Transferencia para Justo · N° {p?.numero}</DialogTitle></DialogHeader>
          {p && (
            <div className="space-y-3">
              <div className="text-sm text-slate-600 space-y-0.5">
                <p>{TIPO_LABEL[p.tipo]} a <b>{p.location_name}</b> · {formatFechaHora(p.fecha)}</p>
                {p.orders_detail?.length > 0 && <p data-testid="justo-maestros">Incluye: {p.orders_detail.map((o) => o.maestro).join(', ')}</p>}
                <p>Origen: <b>Grecia / Bodega Central</b> → Destino: <b>{p.destino_nombre || '—'}</b></p>
                <p>Estado: <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${ESTADOS_JUSTO[p.estado_justo]?.className}`} data-testid="justo-estado">{ESTADOS_JUSTO[p.estado_justo]?.label}</span>
                  {p.justo_export && <span className="text-xs text-slate-400"> · exportado por {p.justo_export.exportado_por}</span>}</p>
              </div>
              {!p.exportable && <div className="rounded-lg bg-slate-100 p-3 text-sm text-slate-600" data-testid="justo-no-exportable">{p.motivo_no_exporta}</div>}
              {p.origenes_disponibles?.length > 0 && <CorregirOrigen p={p} onDone={refresh} />}
              {!isAdmin && !p.exportable && p.motivo_no_exporta?.includes('bodega de origen') && (
                <p className="text-xs text-slate-500" data-testid="justo-pedir-admin-origen">Pide al administrador que corrija la bodega de origen de este despacho.</p>
              )}
              <Avisos p={p} />
              {p.faltantes.length > 0 && <Faltantes items={p.faltantes} isAdmin={isAdmin} />}
              {p.exportable && p.faltantes.length === 0 && (
                <>
                  <div className="border rounded-lg overflow-hidden"><Filas filas={p.filas} /></div>
                  {p.excluidos.length > 0 && <p className="text-xs text-slate-500">No se exportan: {p.excluidos.join(', ')}</p>}
                  <p className="text-xs text-slate-400 font-mono break-all">{p.filename}</p>
                </>
              )}
            </div>
          )}
          <DialogFooter className="gap-2">
            {isAdmin && p?.estado_justo === 'exportado' && (
              <Button variant="outline" onClick={marcar} data-testid="justo-marcar-subido-btn"><CheckCircle2 className="w-4 h-4 mr-1" /> Marcar subido a Justo</Button>
            )}
            <Button onClick={descargar} disabled={busy || !p?.exportable || p?.faltantes.length > 0 || !p?.filas.length}
              className="bg-green-600 hover:bg-green-700" data-testid="justo-descargar-btn">
              <Download className="w-4 h-4 mr-1" /> {p?.estado_justo === 'subido_justo' ? 'Exportar de todas formas' : 'Descargar Excel'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const OrderJustoExport = ({ orderId, status }) => {
  const [env, setEnv] = useState(null);
  useEffect(() => {
    setEnv(null);
    if (orderId) justoAPI.porPedido(orderId).then((r) => setEnv(r.data)).catch(() => setEnv(null));
  }, [orderId, status]);
  return env ? <ExportJustoButton tipo="dispatch" id={env.id} /> : null;
};
